import type { FC } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import { defineMessages } from 'react-intl';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { NarrowPageCardView } from '@confluence/entity-view/entry-points/NarrowPageCardView.ts';
import { RecentWorkListItemView } from '@confluence/entity-view/entry-points/RecentWorkListItemView.ts';

import type { HomeRecentActivitiesQuery_activities_Activities_myActivities_MyActivities_all_ActivitiesConnection_edges_ActivityEdge_node_ActivitiesItem as RecentActivities } from '../Recent/__types__/HomeRecentActivitiesQuery.ts';
import { transformRecentToPageCard } from '../pageCardTransformers.ts';
import { transformActivityData, ActivityEventType } from '../homeViewsQueryHelpers.ts';

import type { HomeRecentWorkQuery_activities_myActivities_all_edges_node as RecentWork } from './__types__/HomeRecentWorkQuery.ts';

const narrowPageCardViewI18n = defineMessages({
	edited: {
		id: 'home.views.overview.recent-work.action.edited.short',
		defaultMessage: 'Edited',
		description:
			'When you last edited the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	visited: {
		id: 'home.views.overview.recent-work.action.visited.short',
		defaultMessage: 'Visited',
		description:
			'When you last visited the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	created: {
		id: 'home.views.overview.recent-work.action.created.short',
		defaultMessage: 'Created',
		description:
			'When you created the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	liked: {
		id: 'home.views.overview.recent-work.action.liked.short',
		defaultMessage: 'Liked',
		description:
			'When you liked the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	commented: {
		id: 'home.views.overview.recent-work.action.commented.short',
		defaultMessage: 'Commented',
		description:
			'When you commented on the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
});

const recentWorkListItemViewI18n: typeof narrowPageCardViewI18n = defineMessages({
	edited: {
		id: 'home.views.overview.recent-work.action.edited.compact',
		defaultMessage: 'You edited {timeAgo} ago',
		description:
			'Activity label on a recent object card. {timeAgo} is compact elapsed time, for example 1h or 2d.',
	},
	visited: {
		id: 'home.views.overview.recent-work.action.viewed.compact',
		defaultMessage: 'You viewed {timeAgo} ago',
		description:
			'Activity label on a recent object card. {timeAgo} is compact elapsed time, for example 1h or 2d.',
	},
	created: {
		id: 'home.views.overview.recent-work.action.created.compact',
		defaultMessage: 'You created {timeAgo} ago',
		description:
			'Activity label on a recent object card. {timeAgo} is compact elapsed time, for example 1h or 2d.',
	},
	liked: {
		id: 'home.views.overview.recent-work.action.liked.compact',
		defaultMessage: 'You liked {timeAgo} ago',
		description:
			'Activity label on a recent object card. {timeAgo} is compact elapsed time, for example 1h or 2d.',
	},
	commented: {
		id: 'home.views.overview.recent-work.action.commented.compact',
		defaultMessage: 'You commented {timeAgo} ago',
		description:
			'Activity label on a recent object card. {timeAgo} is compact elapsed time, for example 1h or 2d.',
	},
});

const ActionMessageKeys: Record<ActivityEventType, keyof typeof narrowPageCardViewI18n> = {
	VIEWED: 'visited',
	EDITED: 'edited',
	UPDATED: 'edited',
	CREATED: 'created',
	PUBLISHED: 'edited',
	LIKED: 'liked',
	COMMENTED: 'commented',
	SNAPSHOTTED: 'edited',
	STARTED: 'created',
	ASSIGNED: 'visited',
	UNASSIGNED: 'visited',
	TRANSITIONED: 'visited',
};

type RecentListItemProps = {
	recent: RecentActivities | RecentWork;
	index: number;
	onItemClick: (
		index: number,
		action: string,
		status: string | undefined,
		contentId: string,
		spaceKey: string,
		tenantId: string,
		objectType: string,
	) => void;
	onItemSpaceClick: (index: number) => void;
	shouldAutofocus?: boolean;
	useAgenticSessionsAppearance?: boolean;
};

export const RecentListItem: FC<RecentListItemProps> = ({
	recent,
	index,
	onItemClick,
	onItemSpaceClick,
	shouldAutofocus,
	useAgenticSessionsAppearance = false,
}) => {
	const { cloudId } = useSessionData();

	const transformedRecent = useMemo(() => transformActivityData(recent, true), [recent]);

	const content = useMemo(
		() => transformRecentToPageCard(transformedRecent as RecentActivities, '', false, true),
		[transformedRecent],
	);
	const action = transformedRecent.event?.eventType || ActivityEventType.VIEWED;
	const { status, id: contentId, type } = content;
	const spaceKey = content.space.spaceKey;

	const linkRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => {
		if (shouldAutofocus && linkRef.current) {
			linkRef.current.focus();
		}
	}, [shouldAutofocus, linkRef]);

	const CardView = useAgenticSessionsAppearance ? RecentWorkListItemView : NarrowPageCardView;
	const i18n = useAgenticSessionsAppearance ? recentWorkListItemViewI18n : narrowPageCardViewI18n;

	return (
		<CardView
			key={index}
			content={content}
			lastActionI18n={i18n[ActionMessageKeys[action]] ?? i18n.visited}
			onClick={() => onItemClick(index, action, status, contentId, spaceKey, cloudId, type)}
			onSpaceClick={() => onItemSpaceClick(index)}
		/>
	);
};
